import {
  defineSearchEngine,
  loadQueryActions,
  type InferHydratedState,
  type InferStaticState,
} from '@coveo/headless-react/ssr';
import { buildSearchEngine, SearchEngine } from '@coveo/headless';
import { generateSearchToken, getValidToken, renewSearchToken } from './token-manager';
import { SearchHub } from './types';
import { engineDefinition, getEngineConfig, NUMBER_OF_RESULTS } from './config';

export { NUMBER_OF_RESULTS };

export type SearchStaticState = InferStaticState<typeof engineDefinition>;
export type SearchHydratedState = InferHydratedState<typeof engineDefinition>;

/**
 * Build a search engine from the default configuration
 * @note Should be called client-side.
 * @param locale the locale of the current route
 * @returns a promise that resolves to a search engine object
 * @see {@link https://docs.coveo.com/en/headless/latest/usage/#configure-a-headless-engine}
 */
export async function buildClientSearchEngine(locale: string): Promise<SearchEngine> {
  const { token: accessToken, searchHub } = await getValidToken();
  const config = getEngineConfig(searchHub as SearchHub);

  return buildSearchEngine({
    configuration: {
      ...config.configuration,
      accessToken,
      search: {
        searchHub,
        // Coveo machine-learning models use locale to provide contextually relevant output:
        locale,
      },
    },
  });
}

/**
 * Builds a search engine from the default configuration, runs a query on
 * the Coveo search engine engine and returns the result.
 * @note Should be called on the server side
 * @param locale the locale of the current route
 * @param query query string
 * @returns the next state of the search engine, after running the query or the state of the search
 */
async function getBuildResult(locale: string, query: string) {
  const { token: accessToken, searchHub } = await generateSearchToken();

  // Create an engine definition with the appropriate static filters for this context
  const contextEngineDefinition = defineSearchEngine(getEngineConfig(searchHub as SearchHub));

  const buildResult = await contextEngineDefinition.build({
    async extend(options) {
      return {
        ...options,
        configuration: {
          ...options.configuration,
          accessToken,
          renewAccessToken: renewSearchToken,
          search: {
            searchHub,
            // Coveo machine-learning models use locale to provide contextually relevant output:
            locale,
          },
        },
      };
    },
  });
  const { updateQuery } = loadQueryActions(buildResult.engine);
  buildResult.engine.dispatch(updateQuery({ q: query }));

  return buildResult;
}

/**
 * Run the query and get the serialized Coveo search engine state (including results)
 * @note server-action.
 * @param locale locale of the current route
 * @param query query string
 * @returns Serialized Coveo search engine state including results
 * @see {@link https://docs.coveo.com/en/headless/latest/usage/headless-server-side-rendering/headless-implement-ssr/#overview}
 */
export async function fetchStaticState(locale: string, query: string): Promise<SearchStaticState> {
  return engineDefinition.fetchStaticState.fromBuildResult({
    buildResult: await getBuildResult(locale, query),
  });
}

/**
 * Hydrate (deserialize) the Coveo search engine state including results
 * @note Should be called client-side.
 * @param options override the current Coveo search action
 * @param locale locale of the current route
 * @param query the query that was last run on the Coveo search engine
 * @returns promise resolving to a 'hydrated' Coveo search engine object including results
 * @see {@link https://docs.coveo.com/en/headless/latest/usage/headless-server-side-rendering/headless-implement-ssr/#overview}
 * @see {@link https://docs.coveo.com/en/1389/build-a-search-ui/standard-actions-and-usage-analytics-reference}
 */
export async function hydrateStaticState(
  options: { searchAction: SearchStaticState['searchAction'] },
  locale: string,
  query: string,
): Promise<SearchHydratedState> {
  return engineDefinition.hydrateStaticState.fromBuildResult({
    buildResult: await getBuildResult(locale, query),
    searchAction: options.searchAction,
  });
}

// re-export components and hooks for interacting with the search engine and controllers:
export const { StaticStateProvider, HydratedStateProvider, setNavigatorContextProvider, useEngine } = engineDefinition;
export const { useSearchBox, useResultList, usePager, useQuerySummary, useStaticFilter, useResultsPerPage } =
  engineDefinition.controllers;
